import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import { presignDraftMedia } from "../uploads/productDraftApi";
import { newMediaId } from "../uploads/fileTypeFromMime";
import type { TaxonomyRichContent, TaxonomyRichMedia, TaxonomyRichMediaType } from "./types";

const ALLOWED_MIME = new Set([
  "image/jpeg", "image/png", "image/heic", "image/bmp",
  "video/mp4", "video/quicktime", "video/x-m4v", "video/webm",
  "audio/mpeg", "audio/mp4", "audio/ogg", "audio/webm",
]);

export function richMediaType(mime: string): TaxonomyRichMediaType | null {
  if (!ALLOWED_MIME.has(String(mime || "").toLowerCase())) return null;
  if (mime.startsWith("image/")) return "IMAGE";
  if (mime.startsWith("video/")) return "VIDEO";
  if (mime.startsWith("audio/")) return "AUDIO";
  return null;
}

function putFile(url: string, file: File): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
}

export async function uploadRichContentMedia(productId: string, file: File): Promise<TaxonomyRichMedia> {
  const type = richMediaType(file.type);
  if (!type) throw new Error("Choose an image, video, or audio file.");
  if (!productId) throw new Error("Save this product as a Draft before adding media.");
  const mediaId = newMediaId();
  const response = await presignDraftMedia(productId, file.type, mediaId);
  const signedUrl = typeof response?.data === "string" ? response.data : "";
  if (!response?.success || !signedUrl) {
    throw new Error(response?.message || "Could not prepare this media upload.");
  }
  await putFile(signedUrl, file);
  return {
    mediaId,
    type,
    mediaURL: signedUrl.split("?")[0],
    fileName: file.name,
  };
}

type Props = {
  value: unknown;
  productId?: string;
  readOnly?: boolean;
  onChange: (value: TaxonomyRichContent | null) => void;
};

export default function RichContentFieldInput({ value, productId, readOnly, onChange }: Props) {
  const current = value && typeof value === "object" ? value as TaxonomyRichContent : {};
  const media = Array.isArray(current.media) ? current.media : [];
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const changeText = (text: string) => onChange({ ...current, text });
  const removeMedia = (mediaId: string) => onChange({
    ...current,
    media: media.filter((item) => item.mediaId !== mediaId),
  });

  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadRichContentMedia(productId || "", file);
      onChange({ ...current, media: [...media, uploaded] });
    } catch (e: any) {
      setError(e?.message || "Media upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return <div className="hkp-tax-rich">
    <Input
      type="textarea"
      rows={3}
      value={typeof current.text === "string" ? current.text : ""}
      disabled={readOnly}
      placeholder="Add text"
      onChange={(event) => changeText(event.target.value)}
    />
    {media.length ? <div className="hkp-tax-rich__media">
      {media.map((item) => <div key={item.mediaId} className="hkp-tax-rich__item">
        <span>{item.type === "IMAGE" ? "Image" : item.type === "VIDEO" ? "Video" : "Audio"}: {item.fileName || item.caption || item.mediaId}</span>
        {item.mediaURL ? <a href={item.mediaURL} target="_blank" rel="noreferrer">View</a> : <span className="text-muted">Unavailable</span>}
        {!readOnly ? <Button type="button" size="sm" color="danger" outline onClick={() => removeMedia(item.mediaId)}>Remove</Button> : null}
      </div>)}
    </div> : null}
    {!readOnly ? <label className="btn btn-sm btn-outline-secondary mt-2 mb-0">
      {uploading ? "Uploading…" : "Add image, video, or audio"}
      <input
        type="file"
        className="visually-hidden"
        accept="image/jpeg,image/png,image/heic,image/bmp,video/mp4,video/quicktime,video/x-m4v,video/webm,audio/mpeg,audio/mp4,audio/ogg,audio/webm"
        disabled={uploading || !productId}
        onChange={(event) => { void upload(event.target.files); event.target.value = ""; }}
      />
    </label> : null}
    {!readOnly && !productId ? <p className="text-muted small mt-1 mb-0">Save this product as a Draft before adding media.</p> : null}
    {error ? <p className="RequiredField mt-1 mb-0" role="alert">{error}</p> : null}
  </div>;
}
